import {
  PhaseStructureTypeEnum,
  RegionAuditInterface,
} from '@vatos-pas/common';

export type RegionAuditCostField = Extract<
  keyof RegionAuditInterface,
  | 'dwHangingCostPerBoard'
  | 'dwFinishingCostPerBoard'
  | 'dwSprayingCostPerBoard'
  | 'dwpHangingCostPerBoard'
  | 'dwpFinishCostPerBoard'
  | 'dwpPrimeCostPerSqft'
  | 'dwpTrim1CostPerSqft'
  | 'dwpBump1CostPerBoard'
  | 'dwpInteriorPaintCostPerSqft'
  | 'dwpTrim2CostPerSqft'
  | 'dwpBump2CostPerBoard'
  | 'dwpFinalPaintCostPerSqft'
  | 'dwpBump3CostPerBoard'
  | 'dwpCMBluetapeCostPerSqft'
  | 'dwpFinalBluetapeCostPerSqft'
  | 'exteriorOnePercentage'
  | 'exteriorTwoPercentage'
>;

// Any column the audit list can render: a real audit-record key plus an
// optional display formatter for non-numeric values.
export interface RegionAuditColumn {
  source: keyof RegionAuditInterface & string;
  label: string;
  sortable: boolean;
  sortBy?: string;
  format?: (value: unknown) => string;
}

export interface AuditColumnConfig extends RegionAuditColumn {
  source: RegionAuditCostField;
}

export const DRYWALL_COST_COLUMNS: AuditColumnConfig[] = [
  {
    label: 'Hang (Per Board)',
    sortable: true,
    source: 'dwHangingCostPerBoard',
  },
  {
    label: 'Finish (Per Board)',
    sortable: true,
    source: 'dwFinishingCostPerBoard',
  },
  {
    label: 'Spray (Per Board)',
    sortable: true,
    source: 'dwSprayingCostPerBoard',
  },
];

export const DRYWALL_AND_PAINT_COST_COLUMNS: AuditColumnConfig[] = [
  {
    label: 'Hang (Per Board)',
    sortable: true,
    source: 'dwpHangingCostPerBoard',
  },
  {
    label: 'Finish (Per Board)',
    sortable: true,
    source: 'dwpFinishCostPerBoard',
  },
  {
    label: 'Prime (Per Sq Ft)',
    sortable: true,
    source: 'dwpPrimeCostPerSqft',
  },
  {
    label: 'Trim 1 (Per Sq Ft)',
    sortable: true,
    source: 'dwpTrim1CostPerSqft',
  },
  {
    label: 'Drywall Touch Up 1 (Per Board)',
    sortable: true,
    source: 'dwpBump1CostPerBoard',
  },
  {
    label: 'Interior Paint (Per Sq Ft)',
    sortable: true,
    source: 'dwpInteriorPaintCostPerSqft',
  },
  {
    label: 'Trim 2 (Per Sq Ft)',
    sortable: true,
    source: 'dwpTrim2CostPerSqft',
  },
  {
    label: 'Drywall Touch Up 2 (Per Board)',
    sortable: true,
    source: 'dwpBump2CostPerBoard',
  },
  {
    label: 'Final Paint (Per Sq Ft)',
    sortable: true,
    source: 'dwpFinalPaintCostPerSqft',
  },
  {
    label: 'Drywall Touch Up 3 (Per Board)',
    sortable: true,
    source: 'dwpBump3CostPerBoard',
  },
  {
    label: 'CM Bluetape (Per Sq Ft)',
    sortable: true,
    source: 'dwpCMBluetapeCostPerSqft',
  },
  {
    label: 'Final Bluetape (Per Sq Ft)',
    sortable: true,
    source: 'dwpFinalBluetapeCostPerSqft',
  },
  {
    label: 'Exterior One',
    sortable: true,
    source: 'exteriorOnePercentage',
  },
  {
    label: 'Exterior Two',
    sortable: true,
    source: 'exteriorTwoPercentage',
  },
];

export const getCostColumnsForWorkflow = (
  phaseStructureType: PhaseStructureTypeEnum,
): AuditColumnConfig[] =>
  phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint
    ? DRYWALL_AND_PAINT_COST_COLUMNS
    : DRYWALL_COST_COLUMNS;

const REGION_AUDIT_PRODUCTION_COST_FIELDS: RegionAuditCostField[] = [
  ...DRYWALL_COST_COLUMNS.map(c => c.source),
  ...DRYWALL_AND_PAINT_COST_COLUMNS.map(c => c.source),
];

// Always visible regardless of phase structure type, so kept out of the
// workflow column arrays. `as const` pins `source` to its literal so the list
// view's record indexing checks it is a real RegionAuditInterface key.
export const REGION_AUDIT_SPECIAL_JOBS_COLUMN = {
  source: 'specialJobsPercentage',
  label: 'Special Jobs',
  sortable: true,
} as const;

// Always visible regardless of phase structure type, so kept out of the
// workflow column arrays.
export const REGION_AUDIT_ALLOW_PAYOUT_EDITING_COLUMN = {
  source: 'allowPayoutAmountEditing',
  label: 'Allow Payout Amount Editing',
  sortable: true,
  format: (value: unknown) => (value ? 'ON' : 'OFF'),
} as const;

// Piecework changes deliberately produce no history row.
export const REGION_AUDIT_WATCH_FIELDS: Array<
  keyof RegionAuditInterface & string
> = [
  ...REGION_AUDIT_PRODUCTION_COST_FIELDS,
  REGION_AUDIT_SPECIAL_JOBS_COLUMN.source,
  REGION_AUDIT_ALLOW_PAYOUT_EDITING_COLUMN.source,
];
